import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Login.module.scss';

const Login: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (value: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setEmailError('');
    setPasswordError('');

    let hasError = false;

    if (!email) {
      setEmailError('Email is required');
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    }

    if (!password) {
      setPasswordError('Password is required');
      hasError = true;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      hasError = true;
    }

    if (hasError) return;

    setLoading(true);
    setTimeout(() => {
      localStorage.setItem(
        'lendsqr_auth',
        JSON.stringify({
          email,
          loggedAt: new Date().toISOString(),
        })
      );
      setLoading(false);
      navigate('/dashboard');
    }, 1500);
  };

  return (
    <div className={styles.loginPage}>

      {/* Left Section */}
      <div className={styles.leftSection}>

        {/* Logo */}
        <div className={styles.logo}>
          <img src={require('../../assets/logo.png')} alt="lendsqr" />
        </div>

        {/* Illustration */}
        <div className={styles.illustration}>
          <svg viewBox="0 0 480 400" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="380" cy="80" r="35" fill="#F5A623" opacity="0.9"/>
            <circle cx="420" cy="200" r="50" fill="#4A90D9" opacity="0.8"/>
            <ellipse cx="350" cy="300" rx="60" ry="40" fill="#E8534A" opacity="0.7"/>
            <rect x="300" y="150" width="50" height="50" rx="4" fill="#39CDCC" opacity="0.6"/>
            <circle cx="100" cy="320" r="30" fill="#F5A623" opacity="0.5"/>
            <ellipse cx="240" cy="370" rx="180" ry="20" fill="#E8E8E8" opacity="0.5"/>
            <rect x="210" y="280" width="22" height="80" rx="10" fill="#2C3E7A"/>
            <rect x="245" y="280" width="22" height="80" rx="10" fill="#2C3E7A" transform="rotate(-10 245 280)"/>
            <ellipse cx="221" cy="358" rx="18" ry="8" fill="#1a1a1a"/>
            <ellipse cx="256" cy="355" rx="18" ry="8" fill="#1a1a1a"/>
            <rect x="195" y="180" width="80" height="110" rx="20" fill="#39CDCC"/>
            <rect x="155" y="220" width="45" height="55" rx="8" fill="#2C3E7A"/>
            <rect x="165" y="210" width="25" height="15" rx="4" fill="#1a1a1a"/>
            <rect x="170" y="235" width="15" height="20" rx="2" fill="#39CDCC" opacity="0.5"/>
            <circle cx="240" cy="155" r="38" fill="#F4C794"/>
            <path d="M205 145 Q215 110 240 108 Q265 110 275 145" fill="#F5A623"/>
            <circle cx="240" cy="120" r="25" fill="#F5A623"/>
            <rect x="220" y="152" width="18" height="14" rx="4" fill="none" stroke="#2C3E7A" strokeWidth="2"/>
            <rect x="244" y="152" width="18" height="14" rx="4" fill="none" stroke="#2C3E7A" strokeWidth="2"/>
            <line x1="238" y1="159" x2="244" y2="159" stroke="#2C3E7A" strokeWidth="2"/>
            <circle cx="226" cy="157" r="4" fill="white"/>
            <circle cx="250" cy="157" r="4" fill="white"/>
            <path d="M230 168 Q240 174 250 168" stroke="#2C3E7A" strokeWidth="2" fill="none" strokeLinecap="round"/>
            <rect x="270" y="100" width="90" height="260" rx="4" fill="#E8E8E8" stroke="#D0D0D0" strokeWidth="2"/>
            <rect x="280" y="110" width="70" height="240" rx="2" fill="white"/>
            <circle cx="282" cy="230" r="5" fill="#D0D0D0"/>
            <rect x="60" y="130" width="110" height="60" rx="12" fill="#FFB5C0"/>
            <path d="M120 190 L110 210 L135 190" fill="#FFB5C0"/>
            <line x1="80" y1="152" x2="150" y2="152" stroke="white" strokeWidth="3" strokeLinecap="round"/>
            <line x1="80" y1="168" x2="130" y2="168" stroke="white" strokeWidth="3" strokeLinecap="round"/>
            <rect x="140" y="340" width="6" height="30" fill="#8BC34A"/>
            <ellipse cx="143" cy="335" rx="10" ry="15" fill="#8BC34A"/>
            <rect x="330" y="345" width="5" height="20" fill="#8BC34A"/>
            <ellipse cx="332" cy="340" rx="7" ry="10" fill="#8BC34A"/>
            <path d="M390 120 L393 130 L403 130 L395 136 L398 146 L390 140 L382 146 L385 136 L377 130 L387 130Z" fill="#F5A623"/>
          </svg>
        </div>
      </div>

      {/* Right Section */}
      <div className={styles.rightSection}>
        <h2>Welcome!</h2>
        <p>Enter details to login.</p>

        <form onSubmit={handleSubmit}>

          <div className={styles.formGroup}>
            <div className={styles.inputWrap}>
              <input
                type="text"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={emailError ? styles.errorInput : ''}
              />
            </div>
            {emailError && <p className={styles.errorMsg}>{emailError}</p>}
          </div>

          <div className={styles.formGroup}>
            <div className={styles.inputWrap}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={passwordError ? styles.errorInput : ''}
              />
              <button
                type="button"
                className={styles.showBtn}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {passwordError && <p className={styles.errorMsg}>{passwordError}</p>}
          </div>

          <span className={styles.forgotLink}>Forgot Password?</span>

          <button
            type="submit"
            className={styles.loginBtn}
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </button>

        </form>
      </div>

    </div>
  );
};

export default Login;